// One place where every API call is made, so that credentials, JSON headers and the meaning of
// a 401 are decided once rather than at each call site.

// A tagged plain Error rather than a subclass of Error. Babel's class transform breaks
// `instanceof` for Error subclasses on the older browsers in this project's browserslist, and a
// session-expiry check that silently stops matching would send the user to a generic failure
// message instead of the sign-in screen.
const taggedError = (message, extra) => Object.assign(new Error(message), extra)

export const isUnauthorized = err => !!(err && err.unauthorized)

// Every request carries the session cookie. `same-origin` is the modern default, but stating it
// means the behaviour does not depend on which default a given browser shipped with.
const request = async (path, { method = 'GET', body } = {}) => {
    const options = { method, credentials: 'same-origin' }
    if (body !== undefined) {
        options.headers = { 'Content-Type': 'application/json' }
        options.body = JSON.stringify(body)
    }

    const res = await window.fetch(path, options)

    if (res.status === 204) return null

    // A body is not guaranteed: an error page from a proxy is not JSON, and parsing must not
    // turn a 502 into an unhandled exception.
    let data = null
    try {
        data = await res.json()
    } catch (err) {
        data = null
    }
    // The API's own message is the useful one ("password must include a symbol"); the status is
    // the fallback for everything else.
    const message = data && data.error ? data.error : null

    // A 401 is tagged rather than replaced, because it means two different things depending on
    // who asked. On a data call the session ended and the caller should show the sign-in screen;
    // on the sign-in call itself it means the credentials were wrong, and the server's wording
    // is what the user needs to read. Keeping the message preserves both readings.
    if (res.status === 401) throw taggedError(message || 'not signed in', { unauthorized: true, status: 401 })

    if (!res.ok) throw taggedError(message || `request failed with status ${res.status}`, { status: res.status })
    return data
}

export const getSession = () => request('/api/auth/me')
export const register = credentials => request('/api/auth/register', { method: 'POST', body: credentials })
export const signIn = credentials => request('/api/auth/login', { method: 'POST', body: credentials })
export const signOut = () => request('/api/auth/logout', { method: 'POST' })

export const listReflections = () => request('/api/reflections')
export const createReflection = payload => request('/api/reflections', { method: 'POST', body: payload })
export const updateReflection = (id, payload) => request(`/api/reflections/${id}`, { method: 'PUT', body: payload })
export const deleteReflection = id => request(`/api/reflections/${id}`, { method: 'DELETE' })
